import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  Search,
  User,
  ShoppingBag,
  ChevronDown,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import { brandAssets, heroMediaV12 } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

const VERSIONS = [
  { label: "V1", href: "/" },
  { label: "V1.2", href: "/v1.2" },
  { label: "V1.3", href: "/v1.3" },
];

// Curated primary navigation — one product, one system, one journey.
const MENU_NAV = [
  { label: "Explore S1", href: "#s1", testid: "menu-nav-s1" },
  { label: "Ways to Stroll", href: "#ways-to-stroll", testid: "menu-nav-ways-to-stroll" },
  { label: "How It Works", href: "#how-it-works", testid: "menu-nav-how-it-works" },
  { label: "Accessories", href: "#accessories", testid: "menu-nav-accessories" },
  { label: "Reviews", href: "#reviews", testid: "menu-nav-reviews" },
  { label: "Safety & Engineering", href: "#safety", testid: "menu-nav-safety" },
  { label: "Support", href: "#support", testid: "menu-nav-support" },
];

const MENU_SECONDARY = ["Our Story", "FAQ", "Shipping & Returns", "Contact"];

const HEADER_H = 76;
const HIDE_AFTER = 120; // never hide within the first 120px
const DELTA = 8; // scroll deadzone to avoid flicker

// V1.3 Refinement 08 — transparent header floating over a full-bleed hero (V1.3 ONLY).
// Adaptive theme: white-on-media over the hero, solid light bar with dark ink past it.
// Smart sticky: hides on scroll down, reveals on scroll up.
export const HeaderV13 = () => {
  const [overHero, setOverHero] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const { pathname } = useLocation();
  const currentVersion = VERSIONS.find((v) => v.href === pathname)?.label ?? "V1";

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const heroH = document.getElementById("hero")?.offsetHeight ?? window.innerHeight;
      setOverHero(y < heroH - HEADER_H);
      const diff = y - lastY;
      if (y <= HIDE_AFTER) setHidden(false);
      else if (diff > DELTA) setHidden(true);
      else if (diff < -DELTA) setHidden(false);
      if (Math.abs(diff) > DELTA || y <= HIDE_AFTER) lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Light theme while the menu is open so it reads against the dimmed backdrop + light panel.
  const light = overHero && !menuOpen;
  const ink = light ? "text-white" : "text-[var(--strollee-ink)]";
  const inkSoft = light ? "text-white/80 hover:text-white" : "text-[var(--strollee-ink)]/75 hover:text-[var(--strollee-ink)]";

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <motion.header
        data-testid="site-header"
        data-theme={light ? "light" : "solid"}
        data-hidden={hidden && !menuOpen}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: hidden && !menuOpen ? -HEADER_H - 8 : 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ease-out ${
          light
            ? "border-b border-transparent bg-transparent"
            : "border-b border-[#D9D6CE]/60 bg-[#FCFBF8]/90 shadow-[0_8px_30px_rgba(23,23,19,0.06)] backdrop-blur-[12px]"
        }`}
      >
        <div
          className="grid grid-cols-[1fr_auto_1fr] items-center px-6 lg:px-10"
          style={{ height: HEADER_H }}
        >
          {/* LEFT — menu trigger (pill, real target) */}
          <div className="justify-self-start">
            <button
              type="button"
              data-testid="header-menu-button"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((open) => !open)}
              className={`flex h-[42px] items-center gap-3 rounded-full border px-4 pr-5 transition-[background-color,border-color,color] duration-200 ${ink} ${
                light
                  ? "border-white/40 bg-white/10 backdrop-blur-md hover:border-white/70 hover:bg-white/20"
                  : "border-[#D9D6CE] bg-transparent hover:border-[var(--strollee-ink)]/60 hover:bg-[var(--strollee-ink)]/[0.04]"
              }`}
            >
              <span className="relative block h-[10px] w-[20px]">
                <span
                  className={`absolute left-0 top-0 h-[2px] w-full rounded-full bg-current transition-all duration-300 ease-out ${
                    menuOpen ? "top-1/2 -translate-y-1/2 rotate-45" : ""
                  }`}
                />
                <span
                  className={`absolute bottom-0 left-0 h-[2px] w-full rounded-full bg-current transition-all duration-300 ease-out ${
                    menuOpen ? "bottom-1/2 translate-y-1/2 -rotate-45" : ""
                  }`}
                />
              </span>
              <span className="text-[13px] font-semibold tracking-wide">
                {menuOpen ? "Close" : "Menu"}
              </span>
            </button>
          </div>

          {/* CENTER — wordmark, truly centered via 1fr/auto/1fr grid */}
          <a
            href="/"
            data-testid="header-logo-link"
            className="flex select-none items-center justify-self-center leading-none"
          >
            <img
              src={light ? brandAssets.logoLight.url : brandAssets.logoDark.url}
              alt={brandAssets.logoDark.alt}
              className="h-[22px] w-auto"
              draggable={false}
            />
          </a>

          {/* RIGHT — utilities + permanent primary CTA */}
          <div className="flex items-center gap-5 justify-self-end">
            <div className="flex items-center gap-4">
              <button
                type="button"
                aria-label="Search"
                data-testid="header-utility-search"
                className={`p-1 transition-all duration-200 hover:-translate-y-[1px] ${inkSoft}`}
              >
                <Search size={19} strokeWidth={1.8} />
              </button>
              <button
                type="button"
                aria-label="Account"
                data-testid="header-utility-account"
                className={`p-1 transition-all duration-200 hover:-translate-y-[1px] ${inkSoft}`}
              >
                <User size={19} strokeWidth={1.8} />
              </button>
              <button
                type="button"
                aria-label="Bag"
                data-testid="header-utility-bag"
                className={`relative p-1 transition-all duration-200 hover:-translate-y-[1px] ${inkSoft}`}
              >
                <ShoppingBag size={19} strokeWidth={1.8} />
                <span
                  className={`absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-semibold ${
                    light
                      ? "bg-white text-[var(--strollee-ink)]"
                      : "bg-[var(--strollee-ink)] text-[var(--strollee-surface)]"
                  }`}
                >
                  0
                </span>
              </button>
            </div>
            <div
              className="relative"
              onMouseEnter={() => setVersionOpen(true)}
              onMouseLeave={() => setVersionOpen(false)}
            >
              <button
                type="button"
                data-testid="header-version-dropdown"
                className={`flex items-center gap-1 text-[11px] font-medium tracking-wide transition-colors duration-200 ${
                  light
                    ? "text-white/50 hover:text-white"
                    : "text-[var(--strollee-muted)]/50 hover:text-[var(--strollee-ink)]"
                }`}
              >
                {currentVersion}
                <ChevronDown
                  size={11}
                  strokeWidth={2}
                  className={`transition-transform duration-200 ${versionOpen ? "rotate-180" : ""}`}
                />
              </button>
              {versionOpen && (
                <div className="absolute right-0 top-full pt-2">
                  <div className="overflow-hidden rounded-xl border border-[var(--strollee-line)] bg-[var(--strollee-surface)] py-1 shadow-[0_8px_24px_rgba(23,23,19,0.10)]">
                    {VERSIONS.map((v) => (
                      <a
                        key={v.label}
                        href={v.href}
                        data-testid={`header-version-option-${v.label.toLowerCase().replace(".", "")}`}
                        className={`block px-4 py-2 text-[11px] font-medium tracking-wide transition-colors duration-150 ${
                          v.label === currentVersion
                            ? "text-[var(--strollee-ink)]"
                            : "text-[var(--strollee-muted)]/60 hover:text-[var(--strollee-ink)]"
                        }`}
                      >
                        {v.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a
              href="#build"
              data-testid="header-build-strollee-cta"
              className="flex h-[42px] items-center rounded-full bg-[var(--strollee-accent)] px-5 text-[13px] font-semibold tracking-wide text-white shadow-[0_4px_16px_rgba(23,23,19,0.18)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[var(--strollee-accent-deep)] hover:shadow-[0_6px_20px_rgba(37,70,51,0.35)]"
            >
              Build my Strollee
            </a>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              key="menu-backdrop"
              data-testid="header-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 z-30 bg-[#0F1A15]/35"
            />
            <motion.div
              key="menu-panel"
              data-testid="header-menu-panel"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="fixed inset-x-6 top-[92px] z-40 overflow-hidden rounded-[24px] lg:inset-x-10 border border-[#D9D6CE]/70 bg-[#FCFBF8] shadow-[0_24px_64px_rgba(23,23,19,0.16)]"
            >
              <div className="grid grid-cols-[1.15fr_1fr] gap-12 p-10 lg:p-12">
                {/* MAIN — large editorial navigation */}
                <div className="flex flex-col">
                  <nav aria-label="Menu" className="flex flex-col">
                    {MENU_NAV.map((item, i) => (
                      <motion.a
                        key={item.label}
                        href={item.href}
                        data-testid={item.testid}
                        onClick={() => setMenuOpen(false)}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, ease: EASE, delay: 0.06 + i * 0.04 }}
                        className="group flex items-center justify-between border-b border-[#E4E1D8] py-3 text-[24px] font-semibold tracking-[-0.02em] text-[var(--strollee-ink)] transition-colors duration-200 hover:text-[var(--strollee-accent)] lg:py-3.5 lg:text-[28px]"
                      >
                        {item.label}
                        <ArrowUpRight
                          size={20}
                          strokeWidth={2}
                          className="-translate-x-1 translate-y-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100"
                        />
                      </motion.a>
                    ))}
                  </nav>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.38 }}
                    className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-2"
                  >
                    {MENU_SECONDARY.map((label) => (
                      <a
                        key={label}
                        href="#"
                        data-testid={`menu-secondary-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => setMenuOpen(false)}
                        className="text-[13px] font-medium text-[var(--strollee-muted)] transition-colors duration-200 hover:text-[var(--strollee-ink)]"
                      >
                        {label}
                      </a>
                    ))}
                  </motion.div>
                </div>

                {/* RIGHT — one strong media area with featured guided path */}
                <motion.div
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: EASE, delay: 0.12 }}
                  className="relative min-h-[400px] overflow-hidden rounded-[18px]"
                >
                  <img
                    src={heroMediaV12.url}
                    alt="Strollee S1 out with a family"
                    data-testid="menu-media"
                    className="absolute inset-0 h-full w-full object-cover"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A15]/70 via-[#0F1A15]/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-8">
                    <p className="text-[13px] font-medium text-white/75">
                      Not sure where to start?
                    </p>
                    <a
                      href="#find-my-setup"
                      data-testid="menu-featured-cta"
                      onClick={() => setMenuOpen(false)}
                      className="group mt-1.5 inline-flex items-center gap-2 text-[22px] font-semibold tracking-[-0.01em] text-white"
                    >
                      Find My Setup
                      <ArrowRight
                        size={18}
                        strokeWidth={2.2}
                        className="transition-transform duration-200 group-hover:translate-x-1"
                      />
                    </a>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
